/**
 * The icon set.
 *
 * Drawn inline rather than imported: an icon font or an SVG sprite would be the
 * portal's only asset dependency, and there are few enough glyphs here that the
 * paths cost less than the loader would. Every icon is a 20×20 stroke drawing
 * on `currentColor`, so an icon takes the colour of the text beside it and
 * needs no variant per surface.
 */

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 20, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* --- Navigation ---------------------------------------------------------- */

/** Three bars on a baseline: the Dashboard's counts. */
export const DashboardIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.33 16.67h13.34" />
    <path d="M5.83 13.33V10M10 13.33V5.83M14.17 13.33V8.33" />
  </Icon>
);

export const SourcesIcon = (props: IconProps) => (
  <Icon {...props}>
    <ellipse cx="10" cy="5" rx="6.25" ry="2.5" />
    <path d="M3.75 5v10c0 1.38 2.8 2.5 6.25 2.5s6.25-1.12 6.25-2.5V5" />
    <path d="M3.75 10c0 1.38 2.8 2.5 6.25 2.5s6.25-1.12 6.25-2.5" />
  </Icon>
);

export const TransferIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 7h13" />
    <path d="m14 4 3 3-3 3" />
    <path d="M20 17H7" />
    <path d="m10 14-3 3 3 3" />
  </Icon>
);

export const DictionaryIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.75 4.17c0-.6.47-1.09 1.06-1.15C6.2 2.9 8.5 3.1 10 4.17c1.5-1.07 3.8-1.27 5.19-1.15.59.06 1.06.55 1.06 1.15v10.1c0 .67-.58 1.19-1.24 1.14-1.35-.1-3.4.1-5.01 1.09-1.6-.99-3.66-1.19-5.01-1.09a1.15 1.15 0 0 1-1.24-1.14z" />
    <path d="M10 4.17v12.33" />
  </Icon>
);

export const SettingsIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M2.92 5.83h9.16M15.42 5.83h1.67M2.92 14.17h1.67M7.92 14.17h9.17M2.92 10h4.16M10.42 10h6.67" />
    <circle cx="13.75" cy="5.83" r="1.67" />
    <circle cx="6.25" cy="14.17" r="1.67" />
    <circle cx="8.75" cy="10" r="1.67" />
  </Icon>
);

/* --- Actions ------------------------------------------------------------- */

export const PlusIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M10 4.17v11.66M4.17 10h11.66" />
  </Icon>
);

export const PlayIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6.67 4.58a.5.5 0 0 1 .76-.43l8 5.42a.5.5 0 0 1 0 .86l-8 5.42a.5.5 0 0 1-.76-.43z" />
  </Icon>
);

export const RefreshIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M16.25 8.33A6.67 6.67 0 0 0 4.6 5.42L3.75 6.25M3.75 11.67a6.67 6.67 0 0 0 11.65 2.91l.85-.83" />
    <path d="M15.83 3.33v2.92h-2.91M4.17 16.67v-2.92h2.91" />
  </Icon>
);

export const TrashIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.33 5.42h13.34M8.33 5.42V4.17c0-.46.37-.84.84-.84h2.66c.47 0 .84.38.84.84v1.25" />
    <path d="M5.42 5.42l.58 10c.03.44.4.78.83.78h6.34c.44 0 .8-.34.83-.78l.58-10" />
    <path d="M8.75 8.75v5M11.25 8.75v5" />
  </Icon>
);

export const PencilIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M13.33 3.75a1.77 1.77 0 0 1 2.5 2.5L7.08 15 3.75 15.83l.84-3.33z" />
  </Icon>
);

export const EyeIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M1.67 10s2.91-5.42 8.33-5.42S18.33 10 18.33 10s-2.91 5.42-8.33 5.42S1.67 10 1.67 10z" />
    <circle cx="10" cy="10" r="2.08" />
  </Icon>
);

export const DownloadIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M10 3.33v8.34M6.25 8.33 10 11.67l3.75-3.34" />
    <path d="M3.75 13.33v1.67c0 .92.75 1.67 1.67 1.67h9.16c.92 0 1.67-.75 1.67-1.67v-1.67" />
  </Icon>
);

export const SignOutIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M7.5 16.67H5c-.92 0-1.67-.75-1.67-1.67V5c0-.92.75-1.67 1.67-1.67h2.5" />
    <path d="M12.92 13.33 16.67 10l-3.75-3.33M16.67 10H7.5" />
  </Icon>
);

export const MenuIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.33 5.83h13.34M3.33 10h13.34M3.33 14.17h13.34" />
  </Icon>
);

export const SearchIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="8.75" cy="8.75" r="5.42" />
    <path d="m16.67 16.67-3.75-3.75" />
  </Icon>
);

export const CloseIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 5l10 10M15 5 5 15" />
  </Icon>
);

/* --- Status -------------------------------------------------------------- */

export const CheckIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4.17 10.42 8.33 14.17l7.5-8.34" />
  </Icon>
);

export const AlertIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M8.57 3.6a1.67 1.67 0 0 1 2.86 0l6.1 10.4a1.67 1.67 0 0 1-1.43 2.5H3.9a1.67 1.67 0 0 1-1.43-2.5z" />
    <path d="M10 7.92v3.33M10 14.17h.01" />
  </Icon>
);

export const InfoIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="10" cy="10" r="7.5" />
    <path d="M10 13.33V10M10 6.67h.01" />
  </Icon>
);

export const ChevronsLeftIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M10.83 5 5.83 10l5 5M15.83 5l-5 5 5 5" />
  </Icon>
);

export const ChevronsRightIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9.17 5l5 5-5 5M4.17 5l5 5-5 5" />
  </Icon>
);

export const ChevronLeftIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12.5 4.17 6.67 10l5.83 5.83" />
  </Icon>
);

export const ChevronRightIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M7.5 4.17 13.33 10 7.5 15.83" />
  </Icon>
);

/**
 * Which way a sortable column is running, and that it can be clicked at all.
 *
 * The one filled icon in the set, and the exception is about size rather than
 * taste: this sits inside a 11px uppercase table heading, and a 1.5px stroke
 * outlining a triangle that small closes up into a smudge. Both carets are
 * drawn always — a column showing one caret only would be a column whose other
 * direction the reader has to discover — and the inactive one is dimmed rather
 * than dropped, so the glyph keeps its width and the heading does not shift
 * when the sort flips.
 *
 * `direction` of null is the unsorted state: both carets faint, which is the
 * affordance that the heading is a control.
 */
export const SortIcon = ({
  direction = null,
  size = 10,
  ...rest
}: Omit<SVGProps<SVGSVGElement>, "direction"> & {
  size?: number;
  direction?: "asc" | "desc" | null;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    <path d="M10 2.5 15.5 9h-11z" opacity={direction === null ? 0.3 : direction === "asc" ? 1 : 0.25} />
    <path d="M10 17.5 4.5 11h11z" opacity={direction === null ? 0.3 : direction === "desc" ? 1 : 0.25} />
  </svg>
);

export const KeyIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="6.67" cy="13.33" r="3.33" />
    <path d="M9.17 10.83 16.67 3.33M13.33 6.67l2.09 2.08M11.25 8.75l2.08 2.08" />
  </Icon>
);

/**
 * Which way a statistic ranks, beside the words that say so.
 *
 * An arrow and not a caret: this sits on a 13px line in the Statistic menu's
 * detail panel, where there is room for the 1.5px stroke the rest of the set
 * uses, and a filled triangle at that size reads as a sort control — which is
 * what `SortIcon` is, three screens away, and the two must not be confused.
 *
 * It never appears without the words. Direction is the fact a reviewer came to
 * the panel for, and an arrow alone asks them to know which way up means good.
 */
export const DirectionIcon = ({
  higherIsBetter,
  ...rest
}: IconProps & { higherIsBetter: boolean }) => (
  <Icon {...rest}>
    {higherIsBetter ? (
      <path d="M10 16.67V3.33M4.58 8.75 10 3.33l5.42 5.42" />
    ) : (
      <path d="M10 3.33v13.34M4.58 11.25 10 16.67l5.42-5.42" />
    )}
  </Icon>
);
